import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { getCccApiBase } from '../config';

export type CccNotification = {
  id: number;
  title?: string;
  message?: string;
  notification_type?: string;
  priority?: string;
  read?: boolean;
  created_at?: string;
  action_url?: string | null;
  is_expired?: boolean;
  meta_data?: {
    source?: string;
    offset_minutes?: number;
    start_at?: string;
    end_at?: string;
    event_title?: string;
    client_label?: string;
    notes?: string;
  } | null;
};

const authHeaders = () => {
  const token = localStorage.getItem('jwt_token') || localStorage.getItem('access_token');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

async function listNotifications(): Promise<CccNotification[]> {
  const { data } = await axios.get(`${getCccApiBase()}/api/ccc/notifications`, {
    headers: authHeaders(),
  });
  return Array.isArray(data?.notifications) ? data.notifications : [];
}

export function useCccNotifications() {
  const [notifications, setNotifications] = useState<CccNotification[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchNotifications = useCallback(async () => {
    const token = localStorage.getItem('jwt_token') || localStorage.getItem('access_token');
    if (!token) {
      setNotifications([]);
      return;
    }
    setLoading(true);
    try {
      setNotifications(await listNotifications());
    } catch {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchNotifications();
    const timer = window.setInterval(() => {
      void fetchNotifications();
    }, 30000);
    return () => window.clearInterval(timer);
  }, [fetchNotifications]);

  const markAsRead = useCallback(
    async (id: number) => {
      await axios.post(`${getCccApiBase()}/api/ccc/notifications/${id}/read`, {}, {
        headers: authHeaders(),
      });
      await fetchNotifications();
    },
    [fetchNotifications]
  );

  const markAllAsRead = useCallback(async () => {
    await axios.post(`${getCccApiBase()}/api/ccc/notifications/read-all`, {}, {
      headers: authHeaders(),
    });
    await fetchNotifications();
  }, [fetchNotifications]);

  const clearNotifications = useCallback(async (scope: 'new' | 'all') => {
    await axios.post(`${getCccApiBase()}/api/ccc/notifications/clear`, { scope }, {
      headers: authHeaders(),
    });
    await fetchNotifications();
  }, [fetchNotifications]);

  return { notifications, loading, fetchNotifications, markAsRead, markAllAsRead, clearNotifications };
}

const CccNotificationsContext = createContext<ReturnType<typeof useCccNotifications> | null>(null);

export function CccNotificationsProvider({ children }: { children: React.ReactNode }) {
  const value = useCccNotifications();
  return React.createElement(CccNotificationsContext.Provider, { value }, children);
}

export function useCccNotificationFeed() {
  const ctx = useContext(CccNotificationsContext);
  if (!ctx) {
    throw new Error('useCccNotificationFeed must be used within CccNotificationsProvider');
  }
  return ctx;
}
